import React from 'react';

import ModalDialog from '@app/common/components/ModalDialog.js';
import Button from '@app/common/components/Button';
import { TariffPlan } from '@app/types/TariffPlan';
import { ModalChangeSubscriptionPlanV2Props, ModalStep } from './types';
import { getModalTitle } from './utils/planUtils';
import Header from './components/Header/Header';
import PlanSelectionStep from './components/PlanSelectionStep/PlanSelectionStep';
import PlanConfirmationStep from './components/PlanConfirmationStep/PlanConfirmationStep';
import { SubscriptionBillingPeriod } from '@app/config';
import { isAgencyUserAccount } from '@app/utils/userUtils';

import styles from './ModalChangeSubscriptionPlanV2.module.scss';

type SelectPlanState =
    | { step: ModalStep.SELECT; selectedPlan: null }
    | { step: ModalStep.CONFIRM; selectedPlan: TariffPlan };

type SelectPlanAction = { type: 'SELECT_PLAN'; payload: TariffPlan } | { type: 'BACK_TO_SELECTOR' };

const initialState: SelectPlanState = {
    step: ModalStep.SELECT,
    selectedPlan: null
};

const selectPlanReducer = (state: SelectPlanState, action: SelectPlanAction): SelectPlanState => {
    switch (action.type) {
        case 'SELECT_PLAN':
            return {
                step: ModalStep.CONFIRM,
                selectedPlan: action.payload
            };
        case 'BACK_TO_SELECTOR':
            return {
                step: ModalStep.SELECT,
                selectedPlan: null
            };
        default:
            return state;
    }
};

const ModalChangeSubscriptionPlanV2: React.FC<ModalChangeSubscriptionPlanV2Props> = ({
    plans,
    isOpen,
    closeModal,
    onPlanChange,
    currentPlan,
    accountType,
    isSaving
}) => {
    const [{ step, selectedPlan }, selectPlanDispatch] = React.useReducer(selectPlanReducer, initialState);
    const [billingFrequency, setBillingFrequency] = React.useState(SubscriptionBillingPeriod.MONTHLY);

    const handlePlanSelect = (plan: TariffPlan) => {
        selectPlanDispatch({ type: 'SELECT_PLAN', payload: plan });
    };

    const handleBackToSelector = () => {
        selectPlanDispatch({ type: 'BACK_TO_SELECTOR' });
    };

    const handleConfirmPlanChange = () => {
        if (selectedPlan && selectedPlan.stripeId) {
            onPlanChange(selectedPlan.stripeId);
        }
    };

    const handleClose = () => {
        closeModal();
    };

    const getLeftButton = () => {
        if (step === ModalStep.CONFIRM) {
            return (
                <Button type="button" secondary onClick={handleBackToSelector} disabled={isSaving}>
                    Back to Plan Selector
                </Button>
            );
        }
        return (
            <Button type="button" tertiary onClick={handleClose} disabled={isSaving}>
                Cancel
            </Button>
        );
    };

    const getRightButton = () => {
        if (step === ModalStep.CONFIRM) {
            return (
                <div className={styles.buttons}>
                    <Button type="button" tertiary onClick={handleClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="button" primary onClick={handleConfirmPlanChange} loading={isSaving}>
                        Change Plan
                    </Button>
                </div>
            );
        }
        return null;
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            closeModal={handleClose}
            leftButton={getLeftButton()}
            rightButton={getRightButton()}
            className={styles.modal}
            bodyClassName={styles.modalBody}
            customHeader
            customHeaderContent={() => (
                <Header step={step} getModalTitle={() => getModalTitle(step)} onClose={handleClose} />
            )}
            shouldBlockBodyScroll
        >
            {step === ModalStep.SELECT ? (
                <PlanSelectionStep
                    billingFrequency={billingFrequency}
                    setBillingFrequency={setBillingFrequency}
                    plans={plans}
                    currentPlan={currentPlan}
                    onPlanSelect={handlePlanSelect}
                    accountType={accountType}
                />
            ) : (
                <PlanConfirmationStep currentPlan={currentPlan} selectedPlan={selectedPlan} accountType={accountType} />
            )}
            {isAgencyUserAccount(accountType) && (
                <div className={styles.disclaimer}>
                    ¹New <em>Upload a Doc</em> limits apply. While you have unlimited access to all other FLK it over
                    forms, this limit helps ensure fair use of additional uploaded documents sent through the platform.
                </div>
            )}
        </ModalDialog>
    );
};

export default ModalChangeSubscriptionPlanV2;
